import React, { useState, useEffect } from 'react';
import api from './api';
import { BrandLockup } from './Logo';

function Login({ onSuccess }) {
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  // The owner has one master password and no email of their own, so that is its own way in.
  const [asOwner, setAsOwner] = useState(false);
  const [forgot, setForgot] = useState(null); // null | 'asking' | 'sent'

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const res = await api.post('/login', { who: asOwner ? 'owner' : email.trim().toLowerCase(), password });
      localStorage.setItem('pm_token', res.data.token);
      // Let in on a temporary password — the app should insist on a real one.
      if (res.data.must_change) localStorage.setItem('pm_must_change', '1');
      else localStorage.removeItem('pm_must_change');
      onSuccess();
    } catch {
      setErr(asOwner ? 'That owner password is not right.' : 'That email address and password don’t match.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={submit}>
        <div style={{ marginBottom: 18 }}><BrandLockup size={42} /></div>
        <p className="hint hint-base" style={{ margin: '0 0 18px' }}>{asOwner ? 'Owner sign-in — master password' : 'Sign in to continue'}</p>

        {!asOwner && (
          <input className="form-input" type="email" autoComplete="username" placeholder="Your email address"
            value={email} onChange={e => setEmail(e.target.value)} style={{ marginBottom: 10 }} />
        )}

        <input
          className="form-input"
          type="password"
          autoFocus
          value={password}
          onChange={e => setPassword(e.target.value)}
          placeholder="Password"
        />
        {err && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%', justifyContent: 'center', marginTop: 16 }}>
          {busy ? 'Checking…' : 'Log in'}
        </button>

        <div className="login-alt">
          <button type="button" className="link-btn" onClick={() => { setAsOwner(v => !v); setErr(''); }}>
            {asOwner ? 'Sign in as a staff member' : 'Sign in as the owner'}
          </button>
          <button type="button" className="link-btn" onClick={() => setForgot('asking')}>Forgotten your password?</button>
        </div>

        {forgot === 'asking' && (
          <div className="login-forgot">
            <div style={{ fontSize: 12.5, marginBottom: 8 }}>We’ll email you a link to set a new one.</div>
            <input className="form-input" type="email" placeholder="Your email address" value={email}
              onChange={e => setEmail(e.target.value)} style={{ marginBottom: 8 }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={async () => {
              // Always the same reply, so this can't be used to find out who works here. The owner's
              // address is asked about too, since the owner has no staff record to match.
              const addr = email.trim();
              try { await api.post(asOwner ? '/owner/forgot' : '/staff/forgot', { email: addr }); } catch { /* same either way */ }
              setForgot('sent');
            }}>Send me a link</button>
          </div>
        )}
        {forgot === 'sent' && (
          <div className="login-forgot">If that address belongs to someone here, a link is on its way. It works once and lasts two days.</div>
        )}
      </form>
    </div>
  );
}

// Wraps the admin app. Shows the login when the backend requires auth and there's
// no valid token; renders children immediately when auth is off (local use).
export default function AuthGate({ children }) {
  const [state, setState] = useState('checking'); // checking | open | needLogin | authed

  useEffect(() => {
    let mounted = true;
    api.get('/auth-status')
      .then(res => {
        if (!mounted) return;
        if (!res.data.authRequired) { setState('open'); return; }
        setState(localStorage.getItem('pm_token') ? 'authed' : 'needLogin');
      })
      .catch(() => { if (mounted) setState('open'); });

    const onUnauth = () => setState('needLogin');
    window.addEventListener('pm-unauthorized', onUnauth);
    return () => { mounted = false; window.removeEventListener('pm-unauthorized', onUnauth); };
  }, []);

  if (state === 'checking') {
    return <div style={{ padding: 48, color: 'var(--text-muted)' }}>Loading…</div>;
  }
  if (state === 'needLogin') {
    return <Login onSuccess={() => setState('authed')} />;
  }
  return children;
}
